import { isValid, parse } from 'date-fns';

import { createFilterHelper } from '@kc-console/shared-ui';

import { DATE_QUERY_FORMAT, isPeriodPreset, toApiDate } from './period';

import type { FilterDefBase, FilterState } from '@kc-console/shared-ui';
import type { PeriodPreset } from './period';

const DATE_QUERY_FILTER_KEYS = ['periodPreset', 'fromDate', 'toDate'] as const;
const DATE_QUERY_FILTER_KEY_SET = new Set<string>(DATE_QUERY_FILTER_KEYS);
const querySyncFilterHelper = createFilterHelper();

export type DateQueryFilterKey = (typeof DATE_QUERY_FILTER_KEYS)[number];

type DateQueryValidationResult =
  | {
      customRange: [Date | null, Date | null];
      isValid: true;
      periodPreset: PeriodPreset | null;
    }
  | {
      customRange: [null, null];
      isValid: false;
      periodPreset: null;
    };

export const dateQuerySyncFilters: FilterDefBase[] = [
  querySyncFilterHelper.text({
    id: 'periodPreset',
    label: '조회 기간',
    enableMultiSelect: false,
  }),
  querySyncFilterHelper.text({
    id: 'fromDate',
    label: '시작일',
    enableMultiSelect: false,
  }),
  querySyncFilterHelper.text({
    id: 'toDate',
    label: '종료일',
    enableMultiSelect: false,
  }),
];

export const isDateQueryFilter = (filter: FilterState) => DATE_QUERY_FILTER_KEY_SET.has(filter.key);

/**
 * 날짜 query는 DataTable UI에 노출하지 않고 query sync 상태에만 포함한다.
 */
export const replaceDateQueryFilters = (
  filters: FilterState[],
  nextDateQuery: Partial<Record<DateQueryFilterKey, string | undefined>>,
) => {
  const visibleFilters = filters.filter(filter => !isDateQueryFilter(filter));
  const preservedDateFilters = filters.filter(
    filter => isDateQueryFilter(filter) && !(filter.key in nextDateQuery),
  );
  const nextDateFilters = DATE_QUERY_FILTER_KEYS.flatMap(key => {
    const value = nextDateQuery[key];
    return value ? [{ key, value }] : [];
  });

  return [...visibleFilters, ...preservedDateFilters, ...nextDateFilters];
};

const getFilterValues = (filters: FilterState[], key: string) =>
  filters.filter(filter => filter.key === key).map(filter => filter.value);

const parseStrictDateQueryValue = (value: unknown) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const date = parse(value, DATE_QUERY_FORMAT, new Date());
  if (!isValid(date)) {
    return null;
  }

  return toApiDate(date) === value ? date : null;
};

/**
 * periodPreset/fromDate/toDate는 하나의 그룹으로 취급해 일부만 잘못되어도 전부 제거한다.
 */
export const validateDateQueryFilters = (filters: FilterState[]): DateQueryValidationResult => {
  const periodPresetValues = getFilterValues(filters, 'periodPreset');
  const fromDateValues = getFilterValues(filters, 'fromDate');
  const toDateValues = getFilterValues(filters, 'toDate');
  const hasDateQuery =
    periodPresetValues.length > 0 || fromDateValues.length > 0 || toDateValues.length > 0;

  if (!hasDateQuery) {
    return {
      customRange: [null, null],
      isValid: true,
      periodPreset: null,
    };
  }

  if (periodPresetValues.length !== 1) {
    return {
      customRange: [null, null],
      isValid: false,
      periodPreset: null,
    };
  }

  const periodPresetValue = periodPresetValues[0];
  if (!isPeriodPreset(periodPresetValue)) {
    return {
      customRange: [null, null],
      isValid: false,
      periodPreset: null,
    };
  }

  if (periodPresetValue !== 'custom') {
    if (fromDateValues.length > 0 || toDateValues.length > 0) {
      return {
        customRange: [null, null],
        isValid: false,
        periodPreset: null,
      };
    }

    return {
      customRange: [null, null],
      isValid: true,
      periodPreset: periodPresetValue,
    };
  }

  if (fromDateValues.length !== 1 || toDateValues.length !== 1) {
    return {
      customRange: [null, null],
      isValid: false,
      periodPreset: null,
    };
  }

  const fromDate = parseStrictDateQueryValue(fromDateValues[0]);
  const toDate = parseStrictDateQueryValue(toDateValues[0]);

  if (!fromDate || !toDate || fromDate > toDate) {
    return {
      customRange: [null, null],
      isValid: false,
      periodPreset: null,
    };
  }

  return {
    customRange: [fromDate, toDate],
    isValid: true,
    periodPreset: periodPresetValue,
  };
};
